import { Box, HStack, useToast } from "@chakra-ui/react";
import { ProgressBar } from "@uppy/react";
import {
  AccountAvatar,
  FormControlDescription,
  PictureUploadInput,
  ApiMessageStack,
} from "components";
import React, { useCallback, useEffect, useMemo } from "react";
import Uppy, { UploadResult } from "@uppy/core";
import XHRUpload from "@uppy/xhr-upload";
import { BaseApi } from "api/base.api";
import "@uppy/core/dist/style.css";
import "@uppy/progress-bar/dist/style.css";
import "styles/uppy-custom.css";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { observer } from "mobx-react";
import { useAppStore } from "utils/react-hooks/useAppStore.hook";

interface IProps {
  accountStore: UserAccountStore;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
}

export const AccountLogoInput: React.FC<IProps> = observer(
  ({ accountStore, onUploadStart, onUploadComplete }) => {
    const {
      authStore: { authToken },
    } = useAppStore();
    const accountData = accountStore.account;
    const toast = useToast();
    const uppy = useMemo(() => {
      return Uppy({
        meta: { type: "avatar" },
        restrictions: {
          maxNumberOfFiles: 1,
          allowedFileTypes: ["image/png", "image/jpg", "image/jpeg"],
        },
        autoProceed: true,
      }).use(XHRUpload, {
        endpoint: `${BaseApi.getBaseUrl()}/accounts/${accountData.id}/logo`,
        method: "POST",
        formData: true,
        fieldName: "file",
        metaFields: [],
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
    }, [accountData, authToken]);

    const handleUploadComplete = useCallback(
      (result: UploadResult<{}, { logoUrl: string; message: string }>) => {
        if (result.successful.length) {
          const url = result?.successful?.[0]?.response?.body?.logoUrl;
          accountStore.upsertLogo(url || null);

          onUploadComplete?.();
          toast({
            ...DEFAULT_SUCCESS_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={"Image uploaded!"} />,
          });
        } else if (result.failed.length) {
          const error = result.failed?.[0]?.response?.body?.message;
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={error} />,
          });

          uppy.cancelAll();
        }
      },
      [accountStore, uppy, toast, onUploadComplete]
    );

    useEffect(() => {
      uppy.on("complete", handleUploadComplete);
      if (!!onUploadStart) {
        uppy.on("upload", onUploadStart);
      }

      return function cleanup() {
        uppy.off("complete", handleUploadComplete);
        if (!!onUploadStart) {
          uppy.off("upload", onUploadStart);
        }
      };
    }, [uppy, handleUploadComplete, onUploadStart]);

    const onChange = useCallback(
      (file?: File) => {
        try {
          if (!!file) {
            uppy.addFile({ data: file, type: file.type });
          }
        } catch (error) {
          toast({
            ...DEFAULT_ERROR_TOAST_OPTIONS,
            description: <ApiMessageStack messageStack={error.message} />,
          });
        }
      },
      [uppy, toast]
    );

    return (
      <HStack spacing={4} align={"center"}>
        <Box>
          <PictureUploadInput
            name={"account-picture"}
            onBlur={() => {}}
            onChange={onChange}
          >
            <AccountAvatar
              account={accountData}
              heightInPx={150}
              bg={"gray.100"}
            />
          </PictureUploadInput>
          <Box mt={1} height={"1rem"}>
            <ProgressBar uppy={uppy} hideAfterFinish={true} />
          </Box>
        </Box>
        <FormControlDescription>
          Please size your logo as you'd like it to appear in your branded email
          reply that is sent to applicants. Images up to 1MB.
        </FormControlDescription>
      </HStack>
    );
  }
);
